/**
 * Mails aus Outlook per Drag & Drop (User 01.11.2026: „Aus Outlook muss das
 * auch klappen, also Mail + Anhänge rüber per Drag & Drop“).
 *
 * Was beim Ziehen aus Outlook im Browser ankommt:
 * - **Klassisches Outlook (Windows)** liefert in Edge/Chrome die Mail als
 *   `.msg`-Datei, ihre Anhänge als normale Dateien — beides landet als echte
 *   Datei im `DataTransfer` und geht den gewöhnlichen Upload.
 * - **Neues Outlook / Outlook im Web** gibt die Mail beim Ziehen nicht als
 *   Datei an andere Webseiten heraus. Dort geht der Umweg über den Desktop
 *   (Ziehen auf den Desktop speichert eine `.eml`), dann die `.eml` ziehen.
 *
 * Nach dem Upload einer `.msg`/`.eml` packt der Server ihre echten Anhänge in
 * einem Schritt als eigene Dokumente aus (Inline-Bilder/Signaturlogos bleiben
 * draußen, keine Dubletten bei erneutem Drop). Verknüpfen wie eine
 * Postfach-Mail lässt sich eine Outlook-Datei nicht — ihr fehlt die
 * Graph-Id —, sie wird nur als Dokument abgelegt.
 */
import { apiUrl } from "../../utils/api";

/** Ist das eine Mail-Datei aus Outlook (`.msg`) bzw. eine `.eml`? */
export function istMailDatei(name: string | null | undefined): boolean {
    return /\.(msg|eml)$/i.test(String(name ?? "").trim());
}

export type MailDateiAuspackErgebnis = {
    imported: number;
    skipped: number;
    failed: number;
    /** Neu angelegte bzw. schon vorhandene Anhang-Dokumente. */
    documentIds: number[];
    linked?: number;
};

/**
 * Alle echten Anhänge einer hochgeladenen Mail-Datei als Dokumente ablegen —
 * oder nur die mit diesen Positionen (`indizes`, aus der Anhangliste der Mail).
 */
export async function mailDateiAuspacken(documentId: number, indizes?: readonly number[]): Promise<MailDateiAuspackErgebnis> {
    const res = await fetch(
        apiUrl(`documents/${documentId}/mail/attachments/import-all`),
        indizes
            ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ indizes }) }
            : { method: "POST" },
    );
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Anhänge der Mail konnten nicht übernommen werden (${res.status})${txt ? `: ${txt}` : ""}`);
    }
    return (await res.json()) as MailDateiAuspackErgebnis;
}

/**
 * Nach dem Upload einer gezogenen Datei: Ist es eine Mail-Datei, ihre Anhänge
 * auspacken. Wirft nie — die Mail selbst liegt bereits; ein Fehler kommt als
 * Satz zurück.
 */
export async function nachMailDateiUpload(
    datei: { name: string },
    dokument: { id?: number | null } | null | undefined,
): Promise<{ anhaenge: number; fehler: string | null; documentIds: number[] } | null> {
    if (!istMailDatei(datei.name) || dokument?.id == null) return null;
    try {
        const r = await mailDateiAuspacken(dokument.id);
        return { anhaenge: r.documentIds.length, fehler: r.failed ? `${r.failed} Anhang/Anhänge von „${datei.name}“ ließen sich nicht übernehmen.` : null, documentIds: r.documentIds };
    } catch (err) {
        return { anhaenge: 0, fehler: err instanceof Error ? err.message : String(err), documentIds: [] };
    }
}
